/**
 * ReferenceModelsPage — Platform admin → Reference Models.
 *
 * Reference Models are SHARED sizing / estimation frameworks (WRICEF
 * Efforts Card, future AMS sizing, CR sizing, etc.). They are NOT rollout
 * implementation templates — Implementation Model remains exclusively for
 * Big Bang / Pilot + Rollout / WRICEF Matrix Template.
 *
 * First model surfaced: **WRICEF Efforts Card** — a Type × Complexity grid
 * with Functional + Technical hours per cell. Used by SmartImprove (and
 * future modules) to auto-size object delivery for each recommendation.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Library, ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const ax = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/admin`,
  withCredentials: true,
});

const COMPLEXITIES = ["Low", "Medium", "High", "Very High"];

export default function ReferenceModelsPage() {
  const [models, setModels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ax.get("/reference-models");
      setModels(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reference models");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading) {
    return <div className="text-sm text-[#6B7280] p-6">Loading reference models…</div>;
  }

  if (editing) {
    return (
      <ReferenceModelEditor
        model={editing}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await reload();
          toast.success("Reference model saved");
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="reference-models-page">
      <header className="mb-5">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979] flex items-center gap-1">
          <Library className="w-3 h-3" /> Platform
        </p>
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          Reference Models
        </h1>
        <p className="text-[11px] text-[#6B7280] mt-0.5 max-w-3xl">
          Shared sizing &amp; estimation frameworks reusable across
          <strong> SmartEvaluate</strong>, <strong>SmartImprove</strong>, and
          future WarehouseX products. Reference Models are <em>not</em> rollout
          templates — they are sizing benchmarks (WRICEF efforts, AMS sizing,
          CR sizing, …).
        </p>
      </header>
      <section className="grid grid-cols-1 gap-4" data-testid="reference-models-list">
        {models.length === 0 ? (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] text-xs rounded-sm px-4 py-3">
            No reference models yet.
          </div>
        ) : null}
        {models.map((m) => (
          <ReferenceModelCard
            key={m.id}
            model={m}
            onEdit={() => setEditing(m)}
          />
        ))}
      </section>
    </div>
  );
}

function ReferenceModelCard({ model, onEdit }) {
  const isWricef = model.kind === "wricef_efforts_card";
  const isClassif = model.kind === "recommendation_classification";
  const totals = model.totals || {};
  const counts = model.counts || {};
  return (
    <article
      data-testid={`reference-model-card-${model.slug}`}
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm hover:border-[#94A3B8] transition"
    >
      <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm uppercase">
              {model.kind}
            </span>
            {model.active === false ? (
              <span className="text-[10px] font-bold uppercase text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-1.5 py-0.5 rounded-sm">
                Inactive
              </span>
            ) : null}
          </div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>
            {model.name}
          </h3>
          {model.description ? (
            <p className="text-[11px] text-[#6B7280] mt-1">{model.description}</p>
          ) : null}
        </div>
        <Button
          onClick={onEdit}
          variant="outline"
          className="rounded-sm text-xs h-7 shrink-0"
          data-testid={`reference-model-edit-${model.slug}`}
        >
          Edit
        </Button>
      </header>
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        {isWricef ? (
          <>
            <Stat label="Types" value={(model.types || []).length} />
            <Stat label="Cells" value={totals.cell_count || 0} />
            <Stat label="Functional" value={`${Math.round(totals.total_functional_hours || 0)} hrs`} />
            <Stat label="Technical" value={`${Math.round(totals.total_technical_hours || 0)} hrs`} />
          </>
        ) : null}
        {isClassif ? (
          <>
            <Stat label="Type Rules" value={counts.type_rules || 0} />
            <Stat label="Strategic Signals" value={counts.strategic_rules || 0} />
            <Stat label="Complexity Signals" value={counts.complexity_rules || 0} />
            <Stat label="Mapping Bands" value={counts.complexity_mapping || 0} />
          </>
        ) : null}
      </div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className="text-sm font-semibold text-[#111827] mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor dispatcher — pick the right editor for the model kind.
// ---------------------------------------------------------------------------
function ReferenceModelEditor({ model, onCancel, onSaved }) {
  if (model.kind === "recommendation_classification") {
    return (
      <ClassificationModelEditor model={model} onCancel={onCancel} onSaved={onSaved} />
    );
  }
  return (
    <WricefEffortsCardEditor model={model} onCancel={onCancel} onSaved={onSaved} />
  );
}

// ---------------------------------------------------------------------------
// Editor — Types (rows, editable) × Complexity (columns, fixed)
// Each cell: Functional hours + Technical hours.
// ---------------------------------------------------------------------------
function emptyCell() {
  return { functional: 0, technical: 0 };
}

function WricefEffortsCardEditor({ model, onCancel, onSaved }) {
  const [name, setName] = useState(model.name || "");
  const [description, setDescription] = useState(model.description || "");
  const [types, setTypes] = useState(model.types || []);
  const [matrix, setMatrix] = useState(model.matrix || {});
  const [saving, setSaving] = useState(false);

  const complexities = model.complexities || COMPLEXITIES;

  // Keep matrix in sync with the live types list.
  const liveMatrix = useMemo(() => {
    const next = {};
    types.forEach((t) => {
      const src = matrix[t] || {};
      next[t] = {};
      complexities.forEach((c) => {
        const cell = src[c] || {};
        next[t][c] = {
          functional: Number(cell.functional) || 0,
          technical: Number(cell.technical) || 0,
        };
      });
    });
    return next;
  }, [types, matrix, complexities]);

  const setCell = (typeName, complexity, field, value) => {
    const next = { ...matrix };
    if (!next[typeName]) next[typeName] = {};
    if (!next[typeName][complexity]) next[typeName][complexity] = emptyCell();
    next[typeName] = {
      ...next[typeName],
      [complexity]: {
        ...next[typeName][complexity],
        [field]: Math.max(0, Number(value) || 0),
      },
    };
    setMatrix(next);
  };

  const renameType = (idx, newName) => {
    const oldName = types[idx];
    const cleaned = newName;
    const nextTypes = [...types];
    nextTypes[idx] = cleaned;
    setTypes(nextTypes);
    if (oldName && oldName !== cleaned) {
      const nextMatrix = { ...matrix };
      if (oldName in nextMatrix) {
        nextMatrix[cleaned] = nextMatrix[oldName];
        delete nextMatrix[oldName];
        setMatrix(nextMatrix);
      }
    }
  };

  const addType = () => {
    // Auto-generate a unique placeholder name
    const used = new Set(types.map((t) => t.toLowerCase()));
    let base = "New Type";
    let candidate = base;
    let n = 2;
    while (used.has(candidate.toLowerCase())) {
      candidate = `${base} ${n}`;
      n += 1;
    }
    const nextTypes = [...types, candidate];
    const nextMatrix = { ...matrix, [candidate]: Object.fromEntries(complexities.map((c) => [c, emptyCell()])) };
    setTypes(nextTypes);
    setMatrix(nextMatrix);
  };

  const removeType = (idx) => {
    const name = types[idx];
    setTypes(types.filter((_, i) => i !== idx));
    if (name) {
      const next = { ...matrix };
      delete next[name];
      setMatrix(next);
    }
  };

  // Totals removed per UX update — the matrix grid is self-explanatory.

  const save = async () => {
    // Block save if duplicate / blank type names exist
    const seen = new Set();
    for (const t of types) {
      const k = (t || "").trim().toLowerCase();
      if (!k) {
        toast.error("WRICEF type names cannot be blank.");
        return;
      }
      if (seen.has(k)) {
        toast.error(`Duplicate WRICEF type name: "${t}"`);
        return;
      }
      seen.add(k);
    }
    setSaving(true);
    try {
      await ax.put(`/reference-models/${model.id}`, {
        name: name.trim() || model.name,
        description: description.trim(),
        types,
        matrix: liveMatrix,
      });
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid={`reference-model-editor-${model.slug}`}>
      <button
        onClick={onCancel}
        className="text-xs text-[#025979] hover:text-[#03738C] flex items-center gap-1 mb-4"
        data-testid="reference-model-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to Reference Models
      </button>
      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            {model.kind}
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-sm text-lg font-bold border-transparent focus-visible:border-[#025979] focus-visible:ring-0 px-0 h-9"
            data-testid="ref-model-name"
          />
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description (purpose, intended consumer)"
            className="rounded-sm text-[11px] text-[#6B7280] border-transparent focus-visible:border-[#025979] focus-visible:ring-0 px-0 h-7 mt-1"
            data-testid="ref-model-description"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="ref-model-save"
        >
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </header>

      <Section
        title="WRICEF Types"
        description="Rename or reorder existing types, or add new ones. The matrix below auto-syncs."
        action={
          <Button
            onClick={addType}
            variant="outline"
            className="rounded-sm text-xs h-7"
            data-testid="ref-type-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add type
          </Button>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ref-types-list">
          {types.length === 0 ? (
            <div className="text-xs text-[#9CA3AF] italic">No WRICEF types defined.</div>
          ) : null}
          {types.map((t, i) => (
            <div key={i} className="flex items-center gap-1 border border-[#E5E7EB] rounded-sm px-2 py-1 bg-white">
              <Input
                value={t}
                onChange={(e) => renameType(i, e.target.value)}
                className="rounded-sm text-xs h-7 border-transparent focus-visible:border-[#025979] focus-visible:ring-0"
                data-testid={`ref-type-name-${i}`}
              />
              <button
                type="button"
                onClick={() => removeType(i)}
                className="text-[#9CA3AF] hover:text-[#B91C1C]"
                aria-label="Remove type"
                data-testid={`ref-type-remove-${i}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Efforts Matrix"
        description="Functional + Technical hours per WRICEF Type × Complexity. Used by SmartImprove (and future modules) to auto-size object delivery."
      >
        {types.length === 0 ? (
          <div className="text-xs text-[#9CA3AF] italic" data-testid="ref-effort-empty">
            Add at least one WRICEF type to populate the matrix.
          </div>
        ) : (
          <EffortsCardGrid
            types={types}
            complexities={complexities}
            matrix={liveMatrix}
            onCellChange={setCell}
          />
        )}
      </Section>
    </div>
  );
}

function Section({ title, description, action, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-5">
      <header className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>{title}</h3>
          {description ? <p className="text-[11px] text-[#6B7280] mt-0.5">{description}</p> : null}
        </div>
        {action}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function EffortsCardGrid({ types, complexities, matrix, onCellChange }) {
  return (
    <div className="overflow-x-auto" data-testid="ref-efforts-card-grid">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 bg-[#F8FAFC] z-10 px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-r border-[#E5E7EB] min-w-[160px]">
              Type \ Complexity
            </th>
            {complexities.map((c) => (
              <th
                key={c}
                colSpan={2}
                className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-[#025979] font-semibold bg-[#E0F2FE] border-b-2 border-r border-[#025979]/20"
                data-testid={`ref-col-${c}`}
              >
                {c}
              </th>
            ))}
          </tr>
          <tr>
            <th className="sticky left-0 bg-white z-10 px-3 py-1 text-left text-[10px] text-[#9CA3AF] border-b border-r border-[#E5E7EB]"></th>
            {complexities.map((c) => (
              <Fragment key={`subhdr-${c}`}>
                <th className="px-2 py-1 text-right text-[10px] text-[#6B7280] border-b border-[#E5E7EB] bg-white">
                  Functional
                </th>
                <th className="px-2 py-1 text-right text-[10px] text-[#6B7280] border-b border-r border-[#E5E7EB] bg-white">
                  Technical
                </th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {types.map((t, ri) => (
            <tr key={t} data-testid={`ref-row-${ri}`}>
              <th className="sticky left-0 bg-white text-left px-3 py-2 font-semibold text-[#111827] border-b border-r border-[#E5E7EB]">
                {t}
              </th>
              {complexities.map((c) => {
                const cell = (matrix[t] && matrix[t][c]) || emptyCell();
                return (
                  <Fragment key={`${t}-${c}`}>
                    <td className="px-1 py-1 border-b border-[#E5E7EB] bg-white">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={cell.functional}
                        onChange={(e) => onCellChange(t, c, "functional", e.target.value)}
                        className="w-20 text-right rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs focus:border-[#025979] focus:outline-none"
                        data-testid={`ref-cell-${ri}-${c}-fn`}
                      />
                    </td>
                    <td className="px-1 py-1 border-b border-r border-[#E5E7EB] bg-white">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={cell.technical}
                        onChange={(e) => onCellChange(t, c, "technical", e.target.value)}
                        className="w-20 text-right rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs focus:border-[#025979] focus:outline-none"
                        data-testid={`ref-cell-${ri}-${c}-te`}
                      />
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalTile() {
  // Retained as a no-op for future totals reintroduction.
  return null;
}

// ---------------------------------------------------------------------------
// Recommendation Classification Model — 4-section editor.
// Sections:
//   1. Type Classification Rules (Keyword | Type | Weight)
//   2. Strategic Classification Rules (Signal | Force Strategic)
//   3. Complexity Scoring Rules (Signal | Score)
//   4. Complexity Mapping (Score Range → Complexity)
// ---------------------------------------------------------------------------
function ClassificationModelEditor({ model, onCancel, onSaved }) {
  const [name, setName] = useState(model.name || "");
  const [description, setDescription] = useState(model.description || "");
  const [typeRules, setTypeRules] = useState(model.type_rules || []);
  const [strategicRules, setStrategicRules] = useState(model.strategic_rules || []);
  const [complexityRules, setComplexityRules] = useState(model.complexity_rules || []);
  const [complexityMapping, setComplexityMapping] = useState(model.complexity_mapping || []);
  const [saving, setSaving] = useState(false);

  const supportedTypes = model.supported_types || [
    "Report", "Interface", "Conversion", "Enhancement",
    "Form", "Workflow", "Config", "Mixed", "Strategic",
  ];
  const complexityLabels = ["Low", "Medium", "High", "Very High"];

  const save = async () => {
    setSaving(true);
    try {
      await ax.put(`/reference-models/${model.id}`, {
        name: name.trim() || model.name,
        description: description.trim(),
        type_rules: typeRules,
        strategic_rules: strategicRules,
        complexity_rules: complexityRules,
        complexity_mapping: complexityMapping,
      });
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid={`reference-model-editor-${model.slug}`}>
      <button
        onClick={onCancel}
        className="text-xs text-[#025979] hover:text-[#03738C] flex items-center gap-1 mb-4"
        data-testid="reference-model-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to Reference Models
      </button>
      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            {model.kind}
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-sm text-lg font-bold border-transparent focus-visible:border-[#025979] focus-visible:ring-0 px-0 h-9"
            data-testid="ref-classif-name"
          />
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description (purpose, intended consumer)"
            className="rounded-sm text-[11px] text-[#6B7280] border-transparent focus-visible:border-[#025979] focus-visible:ring-0 px-0 h-7 mt-1"
            data-testid="ref-classif-description"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="ref-classif-save"
        >
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </header>

      <Section
        title="1. Type Classification Rules"
        description="Map keywords / signals to a WRICEF recommendation type. Higher weights win when multiple keywords match. Used to auto-classify recommendation type."
        action={
          <Button
            onClick={() => setTypeRules([...typeRules, { keyword: "", type: supportedTypes[0] || "Config", weight: 1 }])}
            variant="outline"
            className="rounded-sm text-xs h-7"
            data-testid="classif-type-rule-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add rule
          </Button>
        }
      >
        <EditableTable
          testid="classif-type-rules-table"
          columns={[
            { key: "keyword",  label: "Keyword / Signal", type: "text",   width: "40%", placeholder: "e.g. integration" },
            { key: "type",     label: "Recommendation Type", type: "select", width: "35%", options: supportedTypes },
            { key: "weight",   label: "Weight", type: "number", width: "15%", min: 0, step: 1 },
          ]}
          rows={typeRules}
          onChange={setTypeRules}
          rowKey={(_r, i) => `type-rule-${i}`}
        />
      </Section>

      <Section
        title="2. Strategic Classification Rules"
        description="When any of these signals is present, force the recommendation to be classified as Strategic — overrides normal keyword scoring."
        action={
          <Button
            onClick={() => setStrategicRules([...strategicRules, { signal: "", force_strategic: true }])}
            variant="outline"
            className="rounded-sm text-xs h-7"
            data-testid="classif-strategic-rule-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add rule
          </Button>
        }
      >
        <EditableTable
          testid="classif-strategic-rules-table"
          columns={[
            { key: "signal", label: "Signal", type: "text", width: "70%", placeholder: "e.g. SmartRF" },
            { key: "force_strategic", label: "Force Strategic", type: "boolean", width: "20%" },
          ]}
          rows={strategicRules}
          onChange={setStrategicRules}
          rowKey={(_r, i) => `strategic-rule-${i}`}
        />
      </Section>

      <Section
        title="3. Complexity Scoring Rules"
        description="Signals that contribute to the overall complexity score for a recommendation. Scores are summed; total feeds into the Complexity Mapping below."
        action={
          <Button
            onClick={() => setComplexityRules([...complexityRules, { signal: "", score: 1 }])}
            variant="outline"
            className="rounded-sm text-xs h-7"
            data-testid="classif-complexity-rule-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add rule
          </Button>
        }
      >
        <EditableTable
          testid="classif-complexity-rules-table"
          columns={[
            { key: "signal", label: "Signal", type: "text", width: "70%", placeholder: "e.g. multi-site" },
            { key: "score",  label: "Complexity Score", type: "number", width: "20%", min: 0, step: 1 },
          ]}
          rows={complexityRules}
          onChange={setComplexityRules}
          rowKey={(_r, i) => `complexity-rule-${i}`}
        />
      </Section>

      <Section
        title="4. Complexity Mapping"
        description="Translate the summed score into a complexity band (Low / Medium / High / Very High). Leave Max empty for an open-ended top band."
        action={
          <Button
            onClick={() => setComplexityMapping([...complexityMapping, { min: 0, max: 0, complexity: complexityLabels[0] }])}
            variant="outline"
            className="rounded-sm text-xs h-7"
            data-testid="classif-mapping-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Add band
          </Button>
        }
      >
        <EditableTable
          testid="classif-mapping-table"
          columns={[
            { key: "min",        label: "Min Score", type: "number", width: "20%", min: 0, step: 1 },
            { key: "max",        label: "Max Score", type: "number-nullable", width: "20%", min: 0, step: 1, placeholder: "∞" },
            { key: "complexity", label: "Complexity", type: "select", width: "40%", options: complexityLabels },
          ]}
          rows={complexityMapping}
          onChange={setComplexityMapping}
          rowKey={(_r, i) => `mapping-${i}`}
        />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic editable table — text / number / select / boolean / number-nullable.
// Supports row add (via action button above) + remove + reorder via simple
// up/down arrows. Kept intentionally compact so it slots into any section.
// ---------------------------------------------------------------------------
function EditableTable({ testid, columns, rows, onChange, rowKey }) {
  const updateCell = (i, key, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: value };
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="border border-[#E5E7EB] rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="px-2 py-2 w-10 text-center">#</th>
            {columns.map((c) => (
              <th key={c.key} className="px-3 py-2 font-semibold" style={c.width ? { width: c.width } : undefined}>
                {c.label}
              </th>
            ))}
            <th className="px-2 py-2 w-24 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 2} className="px-3 py-4 text-center text-[11px] text-[#9CA3AF] italic">
                No rules yet — click <strong>Add</strong> above to seed the first row.
              </td>
            </tr>
          ) : null}
          {rows.map((row, i) => (
            <tr key={rowKey(row, i)} className="border-t border-[#F1F5F9]" data-testid={`${testid}-row-${i}`}>
              <td className="px-2 py-1 text-center text-[10px] text-[#9CA3AF]">{i + 1}</td>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-1">
                  <EditableCell
                    column={c}
                    value={row[c.key]}
                    onChange={(v) => updateCell(i, c.key, v)}
                    testid={`${testid}-cell-${i}-${c.key}`}
                  />
                </td>
              ))}
              <td className="px-2 py-1 text-right">
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveRow(i, -1)}
                    disabled={i === 0}
                    className="text-[#9CA3AF] hover:text-[#025979] disabled:opacity-30 disabled:cursor-not-allowed text-[10px] border border-[#E5E7EB] rounded-sm w-5 h-5 flex items-center justify-center"
                    aria-label="Move up"
                    data-testid={`${testid}-up-${i}`}
                  >▲</button>
                  <button
                    type="button"
                    onClick={() => moveRow(i, +1)}
                    disabled={i === rows.length - 1}
                    className="text-[#9CA3AF] hover:text-[#025979] disabled:opacity-30 disabled:cursor-not-allowed text-[10px] border border-[#E5E7EB] rounded-sm w-5 h-5 flex items-center justify-center"
                    aria-label="Move down"
                    data-testid={`${testid}-down-${i}`}
                  >▼</button>
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="text-[#9CA3AF] hover:text-[#B91C1C]"
                    aria-label="Remove row"
                    data-testid={`${testid}-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditableCell({ column, value, onChange, testid }) {
  if (column.type === "select") {
    return (
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs bg-white focus:border-[#025979] focus:outline-none"
        data-testid={testid}
      >
        {(column.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (column.type === "boolean") {
    const checked = value === true || value === "true" || value === 1;
    return (
      <label className="inline-flex items-center gap-1.5 text-[11px] text-[#111827]">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="rounded-sm border-[#E5E7EB] accent-[#025979]"
          data-testid={testid}
        />
        <span className="text-[#6B7280]">{checked ? "Yes" : "No"}</span>
      </label>
    );
  }
  if (column.type === "number") {
    return (
      <Input
        type="number"
        min={column.min}
        step={column.step}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="rounded-sm text-xs h-8 text-right"
        data-testid={testid}
      />
    );
  }
  if (column.type === "number-nullable") {
    // Empty string ↔ null (open-ended)
    const display = (value === null || value === undefined) ? "" : String(value);
    return (
      <Input
        type="number"
        min={column.min}
        step={column.step}
        value={display}
        placeholder={column.placeholder}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : Number(v) || 0);
        }}
        className="rounded-sm text-xs h-8 text-right"
        data-testid={testid}
      />
    );
  }
  return (
    <Input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={column.placeholder}
      className="rounded-sm text-xs h-8"
      data-testid={testid}
    />
  );
}

